import React, { useState, useEffect } from 'react';
import { useToasts } from 'react-toast-notifications';
import { useMutation } from 'react-apollo';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import VideoThumbnail from 'react-video-thumbnail';
import { map } from 'lodash';
import { useForm } from 'react-hook-form';
import {
  Modal, ModalBody, Button, FormGroup, Input, Label,
} from 'reactstrap';
import AWS_S3 from 'aws-sdk/clients/s3';
import bufferFrom from 'buffer-from';
import { MIME_TYPE_REGEX, handleGraphqlError, staticConstants } from '../../utils';
import {
  UPLOAD_MEDIA_FOR_BETA_POST, LOADER_MUTATION, DELETE_MEDIA_FORM_BETA_POST, SENT_BETA_POST_REQUEST,
} from '../../graphql-client/mutations';
import { GET_OFFER_DETAIL_QUERY } from '../../graphql-client/queries';

function CreateBetaPost(props) {
  const {
    isOpen, onClose, offerId, artistId, betaPostData, isModify, betaPostData: {
      shareType: {
        facebook, instagram, twitter, spotify, youtube,
      },
    }, dueDays,
  } = props;
  const {
    register, errors, setError, clearError,
  } = useForm({ nativeValidation: false });
  const { addToast } = useToasts();
  const [modal, setModal] = useState(isOpen);
  const [arrList, setArrList] = useState([]);
  const [deleteImageIndex, setDeleteImageIndex] = useState();
  const [additionalPostInfo, setAdditionalPostInfo] = useState(betaPostData.betaPostInfo || '');
  const [isModalClose, setIsModalClose] = useState(false);

  const s3 = new AWS_S3({
    apiVersion: process.env.REACT_APP_AWS_API_VERSION,
    params: { Bucket: process.env.REACT_APP_S3_BUCKET_NAME },
  });

  useEffect(() => {
    if (betaPostData.media) {
      setArrList(betaPostData.media);
    }
  }, [betaPostData]);

  const toggle = () => {
    setModal(!modal);
    onClose();
  };

  const removePrevImage = async (index) => {
    const values = [...arrList];
    values.splice(index, 1);
    setArrList(values);
  };

  const [loader] = useMutation(LOADER_MUTATION, {
    variables: { show: true },
  });
  const [removeBetaPost] = useMutation(DELETE_MEDIA_FORM_BETA_POST, {
    variables: {},
    refetchQueries: [{
      query: GET_OFFER_DETAIL_QUERY,
      variables: { offerId, artistId },
    }],
    onError(error) {
      loader({ variables: { show: false } });
      addToast(handleGraphqlError(error), { appearance: 'error' });
    },

    onCompleted() {
      loader({ variables: { show: false } });
      removePrevImage(deleteImageIndex);
    },
  });

  const deleteMedia = (index, itemData) => {
    setDeleteImageIndex(index);
    const querObj = {
      offerId,
      artistId,
      segmentId: betaPostData._id,
      mediaId: itemData._id,
    };
    removeBetaPost({ variables: querObj });
  };

  const [loaderMutate] = useMutation(LOADER_MUTATION, {
    variables: { show: true },
  });

  const [uploadMediaForPost] = useMutation(UPLOAD_MEDIA_FOR_BETA_POST, {
    variables: {},
    refetchQueries: [{
      query: GET_OFFER_DETAIL_QUERY,
      variables: { offerId, artistId },
    }],
    onError(err) {
      loaderMutate({ variables: { show: false } });
      addToast(handleGraphqlError(err), { appearance: 'error' });
    },

    onCompleted(response) {
      const responseData = response.createBetaPost;
      loaderMutate({ variables: { show: false } });
      if (responseData.data && responseData.data.media) {
        setArrList(responseData.data.media);
      }
    },
  });

  const [sentLoaderMutate] = useMutation(LOADER_MUTATION, {
    variables: { show: true },
  });

  const [sentBetaPostRequest] = useMutation(SENT_BETA_POST_REQUEST, {
    variables: {},
    refetchQueries: [{
      query: GET_OFFER_DETAIL_QUERY,
      variables: { offerId, artistId },
    }],
    onError(err) {
      sentLoaderMutate({ variables: { show: false } });
      addToast(handleGraphqlError(err), { appearance: 'error' });
    },

    onCompleted(response) {
      sentLoaderMutate({ variables: { show: false } });
      addToast(response.sentBetaPostRequest.message, { appearance: 'success' });
      setIsModalClose(true);
    },
  });

  useEffect(() => {
    if (isModalClose) {
      toggle();
    }
  }, [isModalClose]);

  const handleUploadMedia = (mediaData) => {
    uploadMediaForPost({ variables: mediaData });
    loaderMutate({ variables: { show: true } });
  };

  const handleSentBetaRequest = (postRequestData) => {
    sentBetaPostRequest({ variables: postRequestData });
    loaderMutate({ variables: { show: true } });
  };

  const uploadImageOnS3 = (file, fileObj) => {
    loaderMutate({ variables: { show: true } });
    const fileType = file.name.split('.').pop();
    const type = fileObj.split(';')[0].split('/')[1];
    let s3UploadObj = {
      Key: `${'beta-post'}-${Date.now()}.${fileType}`,
      Body: file,
      ACL: 'public-read',
    };
    if (MIME_TYPE_REGEX.IMAGE.exec(`.${type}`)) {
      const base64Data = bufferFrom(fileObj.replace(/^data:image\/\w+;base64,/, ''), 'base64');
      s3UploadObj = {
        ...s3UploadObj,
        Body: base64Data,
        ContentEncoding: 'base64',
        ContentType: `image/${type}`,
      };
    }
    new Promise((resolve, rej) => {
      if (file) {
        s3.upload(s3UploadObj, (err, ec2Data) => {
          if (err) {
            rej(err);
            loaderMutate({ variables: { show: false } });
          }
          resolve(ec2Data);
          loaderMutate({ variables: { show: false } });
        });
      }
    }).then((data) => handleUploadMedia({
      offerId,
      artistId,
      segmentId: betaPostData._id,
      mediaUrl: data.Location,
      imagePath: s3UploadObj.Key,
      mediaType: MIME_TYPE_REGEX.IMAGE.exec(`.${type}`) ? 'image' : 'video',
    }));
  };

  const handleLogoSubmit = (e) => {
    const file = e[0];
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      uploadImageOnS3(file, reader.result);
    };
  };

  const renderUploadPreview = () => map(arrList, (item, index) => item && (
    <div className="thumb-box" key={`path-${item.url}`}>
      {
        item.mediaType === 'image'
          ? (
            <img
              src={item.url}
              className="image-cover"
              alt="profile"
            />
          )
          : (
            <VideoThumbnail
              videoUrl={item.url}
              snapshotAtTime={2}
            />
          )
      }
      <span className="icon icon-cross" onClick={() => { deleteMedia(index, item); }} />
    </div>
  ));

  useEffect(() => {
    if (arrList) {
      renderUploadPreview();
    }
  }, [arrList]);

  const handleSubmitBetaInfo = () => {
    if (additionalPostInfo.length) {
      clearError('betaPostInfo');
      handleSentBetaRequest({
        offerId,
        artistId,
        segmentId: betaPostData._id,
        betaPostInfo: additionalPostInfo,
      });
    } else {
      setError('betaPostInfo');
    }
  };

  const handleAdditionInfoField = (event) => {
    setAdditionalPostInfo(event.target.value);
    clearError('betaPostInfo');
  };

  return (
    <Modal isOpen={modal} className="modal-create-beta-post">
      <ModalBody>
        <span
          onClick={toggle}
          className="close"
        />
        <h2>{isModify ? 'Modify beta post' : 'Create draft post'}</h2>
        <p>Create your draft post as per the campaign and share with BYGmusic for approval.</p>
        {!isModify && (
        <div className="beta-post-additional-detail">
          <div className="modal-offer-detail-timeline-data">
            {dueDays}
          </div>
          <h3 className="mt-0 mb-2">{betaPostData.campaignTitle}</h3>
          <div dangerouslySetInnerHTML={{ __html: betaPostData.description }} />
          <p>{betaPostData.hashTag}</p>
        </div>
        )}
        <div className="upload-update-wrapper">
          <div className="media-section">
            <div className="signup-form-image-section">
              <Dropzone onDrop={(acceptedFiles) => handleLogoSubmit(acceptedFiles)}>
                {({ getRootProps, getInputProps }) => (
                  <div className="signup-form-image-section" {...getRootProps()}>
                    <input
                      {...getInputProps()}
                      multiple={false}
                      accept="image/*, video/*"
                    />
                    <Label className="m-0">
                      <>
                        <span>
                          Upload media or
                          <strong> browse</strong>
                        </span>
                        <em className="icon icon-camera" />
                      </>
                    </Label>
                  </div>
                )}
              </Dropzone>
            </div>
            <div className="media-thumb">
              {renderUploadPreview()}
            </div>
          </div>
          <FormGroup className={`input-field ${errors.betaPostInfo && `${staticConstants.ERROR_CLASS.FORM_ERROR}`}`}>
            <Input
              type="textarea"
              name="betaPostInfo"
              placeholder="Add information about your post…"
              value={additionalPostInfo}
              onChange={(e) => handleAdditionInfoField(e)}
              innerRef={register({
                required: true,
              })}
            />
          </FormGroup>
        </div>
        <div className="beta-post-footer">
          <div className="share-on">
            {(facebook || instagram || twitter || youtube || spotify) && <span>Share on:</span>}
            <ul>
              {facebook && <li className="facebook" />}
              {instagram && <li className="instagram" />}
              {twitter && <li className="twitter" />}
              {youtube && <li className="youtube" />}
              {spotify && <li className="spotify" />}
            </ul>
          </div>
          <Button
            type="button"
            color="primary"
            onClick={() => {
              handleSubmitBetaInfo();
            }}
          >
            Send for approval
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
}

CreateBetaPost.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  artistId: PropTypes.string.isRequired,
  offerId: PropTypes.string.isRequired,
  isBetaPostAutoApproval: PropTypes.bool,
  betaPostData: PropTypes.shape({
    _id: PropTypes.string,
    betaPostInfo: PropTypes.string,
    campaignTitle: PropTypes.string,
    description: PropTypes.string,
    hashTag: PropTypes.string,
    shareType: PropTypes.shape({
      facebook: PropTypes.bool,
      instagram: PropTypes.bool,
      twitter: PropTypes.bool,
      youtube: PropTypes.bool,
      spotify: PropTypes.bool,
    }),
    media: PropTypes.arrayOf(PropTypes.shape({
      _id: PropTypes.string,
      url: PropTypes.string,
      mediaType: PropTypes.string,
      thumbnail: PropTypes.string,
    })),
  }),
  isModify: PropTypes.bool,
};

CreateBetaPost.defaultProps = {
  betaPostData: {},
  isModify: false,
  isBetaPostAutoApproval: false,
};

export default CreateBetaPost;
